import { FormEvent, useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { HotelSearchCalendar } from '@frontend/features/hotel/components/Availability/Search/HotelSearchCalendar';
import { HotelSearchRoomInput } from '@frontend/features/hotel/components/Availability/Search/HotelSearchRoomInput';
import { countries } from '@frontend/features/common/constants/countries';
import { HotelSearchFilterFields } from './HotelSearchFilterFields';
import { normalizedHotelFilters } from './searchModel';
import { HotelLocationPicker } from './HotelLocationPicker';
import { HotelLocation, HotelSearchDraft, hotelSearchCriteria } from './searchModel';

export function HotelSearchForm({ onSearch, busy }: { onSearch: (draft: HotelSearchDraft) => void; busy: boolean }) {
  const { t, i18n } = useTranslation();
  const [location, setLocation] = useState<HotelLocation | null>(null);
  const [resolving, setResolving] = useState(false);
  const [from, setFrom] = useState<Date | null>(null);
  const [activeCalendar, setActiveCalendar] = useState<'checkIn' | 'checkOut' | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [rooms, setRooms] = useState([{ paxes: [30, 30] }]);
  const [radius, setRadius] = useState('10');
  const [nationality, setNationality] = useState('UY');
  const [filters, setFilters] = useState(normalizedHotelFilters());
  const [error, setError] = useState(false);
  const countryOptions = useMemo(() => {
    let names: Intl.DisplayNames | undefined;
    try {
      names = new Intl.DisplayNames([i18n.language], { type: 'region' });
    } catch {
      /* use existing names */
    }
    return countries
      .map((c) => ({ code: c.code, name: names?.of(c.code) ?? c.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [i18n.language]);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(false);
    try {
      if (!location || !from || !to || resolving) throw new Error('selection');
      const radiusKm = Number(radius);
      if (location.type === 'point' && (!Number.isFinite(radiusKm) || radiusKm <= 0 || radiusKm > 100))
        throw new Error('radius');
      const draft = {
        location,
        checkIn: dayjs(from).format('YYYY-MM-DD'),
        checkOut: dayjs(to).format('YYYY-MM-DD'),
        rooms,
        radiusKm,
        nationality,
        filters,
      };
      hotelSearchCriteria(draft, ['validation']);
      onSearch(draft);
    } catch {
      setError(true);
    }
  };
  return (
    <Box component="form" onSubmit={submit}>
      <Stack gap={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
          <HotelLocationPicker value={location} onChange={setLocation} onResolving={setResolving} />
          {location?.type === 'point' && (
            <TextField
              label={t('hotelSearch.radius')}
              type="number"
              value={radius}
              onChange={(e) => setRadius(e.target.value)}
              inputProps={{ min: 0.1, max: 100, step: 0.1 }}
              size="small"
            />
          )}
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ md: 'center' }}>
          <HotelSearchCalendar
            identifier="checkIn"
            value={from}
            onChange={(date) => {
              setFrom(date);
              if (to && !dayjs(to).isAfter(date, 'day')) setTo(null);
            }}
            minDate={new Date()}
            open={activeCalendar === 'checkIn'}
            onOpen={() => setActiveCalendar('checkIn')}
            onClose={() => setActiveCalendar((current) => (current === 'checkIn' ? null : current))}
            onAccept={() => setActiveCalendar('checkOut')}
          />
          <HotelSearchCalendar
            identifier="checkOut"
            open={activeCalendar === 'checkOut'}
            onOpen={() => setActiveCalendar('checkOut')}
            onClose={() => setActiveCalendar((current) => (current === 'checkOut' ? null : current))}
            value={to}
            onChange={setTo}
            minDate={from ? dayjs(from).add(1, 'day').toDate() : new Date()}
          />
          <Box flex={1}>
            <HotelSearchRoomInput
              setSearchRooms={setRooms}
              searchData={{ location: null, property: null, from, to, rooms }}
            />
          </Box>
          <Autocomplete
            options={countryOptions}
            value={countryOptions.find((c) => c.code === nationality) ?? null}
            getOptionLabel={(c) => c.name}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            onChange={(_e, c) => setNationality(c?.code ?? '')}
            sx={{ minWidth: 190 }}
            renderInput={(params) => <TextField {...params} label={t('hotelSearch.nationality')} size="small" />}
          />
          <Button type="submit" variant="contained" disabled={resolving || busy}>
            {t('hotelSearch.search')}
          </Button>
        </Stack>
        <HotelSearchFilterFields value={filters} onChange={setFilters} />
        {error && <Alert severity="error">{t('hotelSearch.invalid')}</Alert>}
      </Stack>
    </Box>
  );
}
